<script>
import { mapMutations } from "vuex";
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from '@common/componentStore/componentStore';
import { actionStore } from '@common/actionStore';
import { library } from "@common/library/library";
import { configStore } from "@common/configStore";
import { uStore } from "@common/userStore";
import settingsPanels from "@common/settingsEditor/settingsPanels";
import JsonEditorVue from "json-editor-vue";
import { GenerateComponent } from "@common/activityStepPanel/GenerateComponent";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const settingsEditorConfig = {
    cId: "settingsEditor2",
    cTitle: "Settings Editor",
    cType: "settingsEditor",
    showInCmpList: false,
    cConfig: {
        staticPosition: false
    },
    cTemplate: `<div class="component-settings-editor" v-if="cConfig && (editorEnabled || cConfig.staticPosition)">
        <div>
            <div class="settings-header">
                <div class="left-action-area">
                    <div v-if="clientIsAdmin" class="lock-unlock">
                        <span @click="toggleLock" v-if="isLocked">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-lock" viewBox="0 0 16 16">
                                <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                            </svg>
                        </span>
                        <span @click="toggleLock" v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-unlock" viewBox="0 0 16 16">
                                <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2zM3 8a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H3z"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <h1 class="settings-title" v-if="valuesMap">{{ valuesMap.cTitle }}<br/><small>{{valuesMap.cId}}</small></h1>
                <div class="right-action-area">
                    <div>
                        <button v-on:click="setVisibleEditor()">
                            <span class="e-icons e-left-chevron"></span>
                        </button>
                        <button @click="setCurrentView('json', true)">
                            <span class="fa-solid fa-brackets-curly"></span>
                        </button>
                        <button v-on:click="closeEditor({ cId: componentId })">
                            <span class="e-icons e-close"></span>
                        </button>
                    </div>
                    <button v-on:click="toggleMultiColumn()" class="settings-expander" v-if="subPanelColCount > 1">
                        <span class="e-icons e-double-left-arrow" v-if="showMaxColumns"></span>
                        <span class="e-icons e-double-right-arrow" v-if="!showMaxColumns"></span>
                    </button>
                </div>
            </div>
            <div class="component-settings-container" ref="settingsContainer" :style="{ height: sidebarHeight }" :class="{ 'with-sub-panels': hasSubPanels }">
                <div class="json-editor" v-show="currentView === 'json'">
                    <json-editor
                        ref="jsonEditor"
                        v-model="valuesMap"
                    ></json-editor>
                </div>
                <div class="component-settings" v-show="currentView !== 'json'">
                    <div v-if="hasSubPanels" class="sub-container" :style="{ height: subPanelHeight }">
                        <settings-panels
                            :settings-map="settingsMap"
                            :values-map="valuesMap"
                            :is-locked="isLocked"
                            :component-id="componentId"
                            :sub-panel-columns="subPanelColumns"
                            @update:sub-panel-columns="subPanelColumns = $event"
                            @update:sub-panel-col-count="subPanelColCount = $event"
                            :show-max-columns="showMaxColumns"
                            :opened-panel="openedPanel"
                            @update:opened-panel="openedPanel = $event"
                        ></settings-panels>
                    </div>
                    <div v-else class="sub-container container-with-columns" :style="{ height: subPanelHeight }">
                    <div v-for="(columnSettings, columnKey) in settingsMapColumns" :key="columnKey" class="settings-column">
                        <div v-for="(setting, key) in columnSettings">
                            <div v-if="setting.type === 'COMPONENT'">
                                <label-wrapper :label-settings="setting"></label-wrapper>
                                <div v-if="setting.cType === 'logicEditor'">
                                    <component :is="setting.cType"
                                    :cId="setting.cId + '__' + setting.id"
                                    :prop-component-id="componentId"
                                    :disabled="isLocked"
                                    :prop-logic="valuesMap[key]"
                                    @update:prop-logic="valuesMap[key] = $event"
                                    :prop-other-props="setting.otherProps"
                                    @update:prop-other-props="setting.otherProps = $event"></component>
                                </div>
                                <div v-else>
                                    <component :is="setting.cType" :cId="setting.cId + '__' + setting.id" :prop-component-id="componentId"></component>
                                </div>
                            </div>
                            <div v-else>
                                <control :name="key" :prop-config="setting" :values-map="valuesMap" :value="valuesMap[key]" @update:value="valuesMap[key] = $event" :recordId="componentId"></control>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                <div class="final-action-area" :class="{ 'with-sub-panels': hasSubPanels }">
                    <div class="action-area-message">
                        <div v-if="isLocked || !clientIsAdmin">
                            <div v-if="!clientIsAdmin">
                                You do not have permissions.
                            </div>
                            <div v-else>
                                Saving settings are locked, please click on the "lock" icon in the header area of the settings editor.
                            </div>
                        </div>
                        <div v-else>
                            <span v-if="!isLocked && valuesMap.lockedConfig">
                                <strong>Note: The configuration is still locked.  You can save your settings, but it will remain locked to other users.</strong>
                            </span>
                            <span v-else>
                                Click "Save" to save your settings.  Click "Preview" to view the changes without saving.
                            </span>
                        </div>
                    </div>
                    <div class="button-area">
                        <control name="APPLY_PREVIEW"></control>
                        <control name="SAVE_SETTINGS"></control>
                    </div>
                </div>
            </div>
        </div>
    </div>`,
    controls: [
        {
            name: 'SAVE_SETTINGS',
            type: "splitbutton",
            label: 'Save',
            onclick: 'saveSettings',
            onselect: 'selectSubAction',
            cssClass: 'e-info',
            items: [
                { text: 'Delete Component', value: 'delete' }
            ]
        },
        {
            name: 'DELETE_COMPONENT',
            type: 'button',
            label: 'Delete',
            onclick: 'deleteComponent',
            cssClass: 'e-danger'
        },
        {
            name: 'APPLY_PREVIEW',
            type: 'button',
            label: 'Preview',
            onclick: 'applyPreview'
        }
    ]
};

const settingsEditorComp = {
    name: 'settingsEditor',
    props: {
        componentId: {
            default: false
        },
        cId: {
            default: false
        },
        cInfo: {
            default: false
        },
        editorOpened: {
            default: false
        },
        previewId: {
            default: false
        },
        presetView: {
            default: false
        }
    },
    data() {
        return {
            position: 'Right',
            selectedComponentId: null,
            settingsMap: false,
            prevSettings: false,
            tmpValuesMap: false,
            editorConfig: false,
            editorOptions: {},
            templateData: false,
            componentDataTestIds: false,
            cConfig: false,
            editorInitialized: false,
            enableGestures: false,
            tmpTemplateSettings: false,
            sortByOptions: [],
            addedSortBy: [],
            isLocked: false,
            panelHeight: 700,
            unsavedChangesChecked: false,
            windowHeight: window.innerHeight,
            windowWidth: window.innerWidth,
            subPanelColumns: 1,
            subPanelColCount: 1,
            showMaxColumns: false,
            openedPanel: false,
            selectedView: 'default'
        };
    },
    computed: {
        clientIsAdmin() {
            return this && this.user && this.user.CLIENT_IS_ADMIN === 'Yes';
        },
        currentView() { // currentView can be 'json' or 'default'
            if (this.selectedView !== 'default') return this.selectedView;
            return this.presetView || 'default';
        },
        settingsColCount() {
            if (this.hasSubPanels && this.subPanelColumns) return this.subPanelColumns;
            return 1;
        },
        columnCount() {
            let maxColumns = Math.floor(this.windowWidth / 560); // NOSONAR
            if (!this.showMaxColumns) maxColumns = 1;
            return Math.min(this.settingsColCount, maxColumns);
        },
        settingsMapColumns() {
            let output = {};
            for (let prop in this.settingsMap) {
                if (prop === 'length') continue;
                let sColumn = this.settingsMap[prop].column;
                if (sColumn) {
                    let cColumn = sColumn <= this.columnCount ? sColumn : this.columnCount;
                    if (!output['column' + cColumn]) output['column' + cColumn] = {};
                    output['column' + cColumn][prop] = this.settingsMap[prop];
                }
            }
            return output;
        },
        settingsEditorWidth() {
            return this.columnCount * 560; // NOSONAR
        },
        sidebarHeight() {
            return this.panelHeight + "px";
        },
        subPanelHeight() {
            return (this.panelHeight - 10) + "px"; // NOSONAR
        },
        dataTestIdAppended() {
            return this.cId + '_' + this.valuesMap.cId;
        },
        componentCurrentSettings() {
            let settingsToSave = library.cloneObj(this.valuesMap);
            Object.keys(this.settingsMap).forEach((smIdx) => {
                if (this.settingsMap[smIdx].type === 'COMPONENT') {
                    if (this.settingsMap[smIdx].cType === 'logicEditor') {
                        settingsToSave[smIdx] = this.valuesMap[smIdx];
                    }
                    if (this.settingsMap[smIdx].dataToSave) {
                        Object.keys(this.settingsMap[smIdx].dataToSave).forEach((dsIdx) => {
                            let propertyName = this.settingsMap[smIdx].dataToSave[dsIdx].to;
                            let propertyToSave = this.settingsMap[smIdx].dataToSave[dsIdx].from;
                            let storeId = Array.isArray(this.storeId) ? this.storeId[0] : this.storeId;
                            let fromCId = this.settingsMap[smIdx].cId;
                            if (this.$store.state[storeId]
                                && this.$store.state[storeId][fromCId + '__' + smIdx]
                                && this.$store.state[storeId][fromCId + '__' + smIdx][propertyToSave]) {
                                settingsToSave[propertyName] = library.cloneObj(this.$store.state[storeId][fromCId + '__' + smIdx][propertyToSave]);
                            }
                        });
                    }
                } else if (this.settingsMap[smIdx].type.toLowerCase() === 'array' && typeof this.controls[smIdx] !== 'undefined') {
                    settingsToSave[smIdx] = library.cloneObj(this.controls[smIdx].value);
                } else if (typeof this.controls[smIdx] !== 'undefined') {
                    settingsToSave[smIdx] = this.controls[smIdx].value;
                }
            });
            if (settingsToSave['positions']) {
                let components = [];
                let addComp = (comp) => {
                    components.push({
                        cId: comp.cId,
                        cType: comp.cType,
                        cInfo: comp.cInfo
                    });
                };
                Object.keys(settingsToSave['positions']).forEach((pIdx) => {
                    if (settingsToSave['positions'][pIdx].components) {
                        Object.keys(settingsToSave['positions'][pIdx].components).forEach((cIdx) => {
                            addComp(settingsToSave['positions'][pIdx].components[cIdx]);
                        });
                    } else {
                        Object.keys(settingsToSave['positions'][pIdx]).forEach((itemIdx) => {
                            addComp(settingsToSave['positions'][pIdx][itemIdx]);
                        });
                    }
                });
                settingsToSave.components = components;
            }
            return settingsToSave;
        },
        valuesMap: {
            get() {
                this.getEditorConfig();
                let settings = library.cloneObj(this.$store.state.componentStore.components[this.componentId]);
                // set saved values
                if (this.editorConfig && !this.settingsMapped) {
                    this.settingsMapped = true;
                    this.setSettingsMap(settings, this.editorConfig);
                }
                if (!this.tmpValuesMap) {
                    this.tmpValuesMap = settings;
                }
                if (this.tmpTemplateSettings && this.tmpTemplateSettings.newPositions) {
                    this.tmpValuesMap.positions = library.cloneObj(this.tmpTemplateSettings.newPositions);
                    this.tmpTemplateSettings = false;
                }
                return this.tmpValuesMap;
            },
            set(value) {
                this.tmpValuesMap = value;
            }
        },
        hasSubPanels() {
            let output = false;
            for (let prop in this.settingsMap) {
                if (prop === 'length') continue;
                if (this.settingsMap[prop].type === 'panel') {
                    output = true;
                    break;
                }
            }
            return output;
        },
        componentInfo() {
            return new GenerateComponent(this, this.config?.componentInfo, 'componentInfo');
        },
    },
    watch: {
        columnCount(newValue) {
            this.$emit('update:columnCount', newValue);
        }
    },
    methods: {
        ...mapMutations({
            closeEditor: 'settingsEditorStore/closeEditor',
            setVisibleEditor: 'settingsEditorStore/setVisibleEditor'
        }),
        setCurrentView(view, toggle) {
            this.selectedView = toggle && view === this.selectedView ? this.presetView : view;
        },
        getByPath(obj, path) {
            return library.getByPath(obj, path);
        },
        checkForChanges() {
            if (!this.unsavedChangesChecked
                && this.prevSettings !== false && this.prevSettings !== null
                && !library.isEqualObj(this.valuesMap, this.prevSettings)
                && JSON.stringify(this.valuesMap) !== JSON.stringify(this.prevSettings)
                && JSON.stringify(this.valuesMap).toLowerCase() !== JSON.stringify(this.prevSettings).toLowerCase()
            ) {
                library.showDialog("You have unsaved changes for " + this.componentId + ". <br/>Click \"Don't Save\" to close the settings for " + this.componentId + " without saving.<br/>Click \"Save\" to stay in the settings.",
                    () => {
                        this.saveSettings();
                    },
                    async () => {
                        this.unsavedChangesChecked = true;
                        this.valuesMap = library.cloneObj(this.$store.state.componentStore.components[this.componentId]);
                        this.prevSettings = library.cloneObj(this.$store.state.componentStore.components[this.componentId]);
                    },
                    {
                        title: "Confirm to Continue",
                        okButtonLabel: "Save",
                        cancelButtonLabel: "Don't Save",
                        preventCloseOnBackdropClick: true
                    });
            }
        },
        toggleLock: function () {
            if (this.isLocked) {
                let secretValue = '';
                let lockTemplate = {
                    name: "lock-template",
                    template: `<div><control name="SECRET" :prop-config="secretConfig" :value="secretValue" @update:value="secretValue = $event"></control></div>`,
                    data() {
                        return {
                            secretValue: secretValue,
                            secretConfig: {
                                name: 'SECRET',
                                type: 'string',
                                label: 'Please enter the secret key to unlock',
                                helpText: 'Please type UNLOCK in all caps.'
                            }
                        };
                    },
                    watch: {
                        'secretValue': function (newVal) {
                            secretValue = newVal;
                        }
                    },
                    components: {
                        control: controlComponent,
                        settingsPanels: settingsPanels
                    }
                };
                library.showDialog(lockTemplate, () => {
                    if (secretValue === 'UNLOCK') {
                        this.isLocked = false;
                        this.updateLockedControls();
                    } else if (secretValue === 'UNLOCK_AND_SAVE') {
                        this.isLocked = false;
                        this.updateLockedControls(true);
                    }
                }, { title: 'Unlock settings editor' });
            } else {
                this.isLocked = true;
                this.updateLockedControls(true); // Auto save locked state
            }
        },
        updateLockedControls: async function (andSave, fromUpdated) {
            let locked = this.isLocked;
            if (!fromUpdated) {
                this.valuesMap.lockedConfig = locked;
                let lockedLabel = locked ? 'Locked' : 'Unlocked';
                if (andSave) {
                    await this.sDs.post('sds_setComponentLocked', this.componentId, locked);
                }
                let successMsg = andSave ? 'Component Fully ' + lockedLabel + '!' : 'Component Partially ' + lockedLabel + '!';
                this.saveSettings(false, false, true, false, false, successMsg);
            }
        },
        toggleMultiColumn() {
            this.showMaxColumns = !this.showMaxColumns;
        },
        async applyPreview(event, item) {
            await this.saveSettings(event, item, true);
        },
        saveSettings: async function (event, item, preview, componentIdChecked, prevComponentId, successMsg) {
            this.unsavedChangesChecked = true;
            let settingsToSave = library.cloneObj(this.componentCurrentSettings);
            // TODO ALLOW "enable test mode" to console log or other type of log, and not save to database.
            delete settingsToSave.cConfig;
            let validationErrors = [];
            for (let controlName in this.controls) {
                if (controlName === 'length') continue;
                if (this.controls[controlName]
                    && typeof settingsToSave[controlName] !== 'undefined'
                    && typeof this.controls[controlName].validationErrors === 'object'
                    && this.controls[controlName].validationErrors.length) {
                    validationErrors = validationErrors.concat(this.controls[controlName].validationErrors);
                }
                if (controlName === 'cId'
                    && typeof settingsToSave[controlName] !== 'undefined'
                    && /\s/.test(settingsToSave[controlName])) {
                    validationErrors = validationErrors.concat([this.controls[controlName].label + ' should not contain spaces.']);
                }
            }
            if (validationErrors.length) {
                library.showDialog(validationErrors.join('<br/>'), false, false, { showAsAlert: true });
                return;
            }
            if (!preview && !componentIdChecked && settingsToSave.cId !== this.componentId) {
                let choiceValue = '1';
                let choiceTemplate = {
                    name: "choice-template",
                    template: `<div>
                                    <control name="CHOICE" :prop-config="choiceConfig" :value="choiceValue" @update:value="choiceValue = $event"></control>
                               </div>`,
                    data() {
                        return {
                            choiceValue: choiceValue,
                            choiceConfig: {
                                name: 'CHOICE',
                                type: 'radio_button_list',
                                label: 'Component ID was changed, please select the option for save:',
                                items: [
                                    { label: 'Change Component ID only.', value: '1' },
                                    { label: 'Save as new.', value: '2' },
                                ],
                            }
                        };
                    },
                    watch: {
                        'choiceValue': function (newVal) {
                            choiceValue = newVal;
                        }
                    },
                    components: {
                        control: controlComponent,
                        settingsPanels: settingsPanels
                    }
                };
                library.showDialog(choiceTemplate, () => {
                    if (choiceValue === '1') {
                        this.saveSettings(event, item, preview, true, this.componentId, successMsg);
                    } else {
                        this.saveSettings(event, item, preview, true, false, successMsg);
                    }
                });
                return;
            }
            if (prevComponentId) {
                settingsToSave.prevCId = prevComponentId;
            }
            library.showLoadingIndicator();
            let componentId = await componentStore.saveComponent(settingsToSave, preview, this.componentId, this.previewId, false, successMsg);
            if (componentId && componentId !== this.componentId && actionStore) {
                actionStore.executeGlobalMethod('componentList', 'selectComponent', [{ cId: componentId }, true], this.parentStoreId);
            }
            library.hideLoadingIndicator();
            // this.closeSettingsSidebar();
        },
        selectSubAction(event) {
            if (event.item && event.item.value === 'delete') {
                this.deleteComponent();
            }
        },
        setSelectedComponent(event) {
            if (event.responseObj
                && event.control
                && event.responseObj.responses[0]
                && event.responseObj.responses[0] !== 'failure'
            ) {
                this.controls[event.control.name].value = event.responseObj.responses[0];
            }
            return 'success';
        },
        deleteComponent: async function () {
            let cId = this.componentId || this.selectedComponentId;
            let iConfig = await componentStore.getConfig(cId);
            if (iConfig.lockedConfig) {
                library.showDialog('You cannot delete this component because it is locked.', false, false, { showAsAlert: true });
                return false;
            }
            library.showDialog("Are you sure you want to delete this component?",
                async () => {
                    this.unsavedChangesChecked = true;
                    this.prevSettings = false;
                    if (actionStore) {
                        actionStore.executeGlobalMethod('componentList', 'selectComponent', [{ cId: false }, true], this.parentStoreId);
                    }
                    this.$nextTick(async () => {
                        await componentStore.deleteComponent(cId);
                        actionStore.executeGlobalMethod('componentPreview', 'selectComponent', [{ cId: false }, true], this.parentStoreId);
                        actionStore.executeGlobalMethod('componentList', 'reloadComponents', [{ cId: false }, true], this.parentStoreId);
                    });
                },
                false,
                {
                    title: "Confirm to Continue"
                });
            return true;
        },
        convertDataType(data, type) {
            switch (type) {
                case 'BOOLEAN':
                    return !!+data;
                case 'NUMBER':
                    return +data;
                default:
                    return data;
            }
        },
        async onPropsUpdate() {
            await this.initEditor();
        },
        async initEditor() {
            // Don't initiate the editor if it's not enabled.
            if (this.editorInitialized || !this.editorEnabled) return;
            // setup configStore
            await library.promiseAllSettled([
                configStore.getAccountSettings(),
                configStore.getRecruiterSettings(),
                uStore().getUserData(),
                configStore.getDatasetList()
            ]);
            // Get the config for this settingsEditor
            this.cConfig = await componentStore.getConfig(this.cId || 'settingsEditor');
            this.editorInitialized = true;
            // if (!this.cConfig.staticPosition) this.toggleSettingsSidebar();
        },
        async getEditorConfig() {
            // Exit if we already have editorConfig
            if (this.editorConfig) return;
            let configs = await componentStore.get('componentsConfig');
            let ourConfig = {};
            let ourComponent = configs[this.valuesMap.cType];
            if (ourComponent) {
                ourConfig = ourComponent.editorSettings;
                // TODO maybe move this?
                this.componentDataTestIds = ourComponent.dataTestIds || [];
            }
            // Exit if we don't have a config
            if (!ourConfig) return;
            let hasPanels = () => {
                for (let iKey in ourConfig) {
                    if (iKey === 'length') continue;
                    if (ourConfig[iKey].type === 'panel') return true;
                }
                return false;
            };
            // Set panel for default
            let hasSubPanels = hasPanels();
            if (typeof ourConfig.commonSettingsPanel === 'undefined' && hasSubPanels) {
                ourConfig.commonSettingsPanel = {
                    type: 'panel',
                    label: 'Common Component Settings',
                    id: 'commonSettingsPanel',
                    fields: []
                };
            }
            if (typeof ourConfig.cTitle === 'undefined') {
                ourConfig.cTitle = {
                    label: 'Component Title',
                    type: 'string',
                    id: 'cTitle',
                    required: true,
                    helpText: 'The component title is used for easy reference when viewing components in the devDash.'
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('cTitle');
            }
            if (typeof ourConfig.cId === 'undefined') {
                ourConfig.cId = {
                    label: 'Component ID',
                    type: 'string',
                    id: 'cId',
                    required: true,
                    message: 'WARNING: Changing this can cause systems to break where this component is being referenced.',
                    helpText: 'The component ID is what you will use to reference this component in your layouts, page configs, or templates.'
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('cId');
            }
            if (typeof ourConfig.cInfo === 'undefined') {
                ourConfig.cInfo = {
                    label: 'Component Description',
                    type: 'text',
                    id: 'cInfo',
                    required: false,
                    default: '<h4>To add component information and description, go to Common Component Settings.</h4>',
                    message: '',
                    helpText: 'Write a description that describes this component, its purpose, and how it will be used. You may use links.'
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('cInfo');
            }
            if (typeof ourConfig.lockedConfig === 'undefined') {
                ourConfig.lockedConfig = {
                    label: 'Lock Config',
                    type: 'boolean',
                    id: 'lockedConfig',
                    helpText: 'Lock the configuration to prevent the component from being edited or deleted.',
                    default: false
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('lockedConfig');
            }
            if (typeof ourConfig.isPublic === 'undefined') {
                ourConfig.isPublic = {
                    label: 'Component Is Public',
                    type: 'boolean',
                    id: 'isPublic',
                    default: false
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('isPublic');
            }
            // Set some default editor settings for all components
            if (typeof ourConfig.componentStyleInfo === 'undefined') {
                ourConfig.componentStyleInfo = {
                    label: 'Component Style Info',
                    type: 'info',
                    id: 'componentStyleInfo',
                    text: 'Below is generic component level styling.  These styles will override the parent styling applied at the layout level.',
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('componentStyleInfo');
            }
            if (typeof ourConfig.useCommonComponentStyles === 'undefined') {
                ourConfig.useCommonComponentStyles = {
                    type: 'boolean',
                    label: 'Use Common Component styles',
                    id: 'useCommonComponentStyles',
                    default: true
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('useCommonComponentStyles');
            }
            if (typeof ourConfig.commonComponentStyles === 'undefined') {
                ourConfig.commonComponentStyles = {
                    type: 'object',
                    label: 'Common Component Settings Override',
                    helpText: 'Override settings for common component settings.',
                    id: 'commonComponentStyles',
                    default: {},
                    properties: {
                        componentBackgroundColor: {
                            label: 'Component Background Color',
                            type: 'color',
                            id: 'componentBackgroundColor',
                            helpText: 'The base level background color for the component.  Other background color settings might also exists depending on the component.'
                        },
                        componentPadding: {
                            label: 'Component Padding',
                            type: 'string',
                            id: 'componentPadding',
                            helpText: 'The padding around your component.',
                            message: 'eg: 50px, 5em or 10px 5px 3px 1px'
                        },
                        componentBoxShadow: {
                            type: 'string',
                            label: 'Box Shadow Settings',
                            helpText: 'Display a box shadow for all child components.',
                            id: 'componentBoxShadow',
                            message: 'eg: 0 0 15px rgba(0,0,0, 0.15)'
                        },
                        componentBorderRadius: {
                            id: 'componentBorderRadius',
                            type: 'dropdown',
                            items: [
                                { text: '0 none', value: '1' },
                                { text: '3px', value: '2' },
                                { text: '5px', value: '3' },
                                { text: '10px', value: '4' },
                            ],
                            fields: {
                                text: 'text',
                                value: 'value'
                            },
                            label: 'Component Border Radius',
                            helpText: 'Set the border radius of child components.',
                        }
                    },
                    logic: [
                        {
                            record: 'self',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: '!this.valuesMap.useCommonComponentStyles'
                        }
                    ]
                };
                if (hasSubPanels) ourConfig.commonSettingsPanel.fields.push('commonComponentStyles');
            }
            if (typeof ourConfig.commonStylesheetPanel === 'undefined' && hasSubPanels) {
                ourConfig.commonStylesheetPanel = {
                    type: 'panel',
                    label: 'Component Styles',
                    id: 'commonStylesheetPanel',
                    fields: []
                };
            }
            if (typeof ourConfig.componentCssStylesheet === 'undefined') {
                ourConfig.componentCssStylesheet = {
                    type: 'text',
                    label: 'Component Specific Stylesheet',
                    id: 'componentCssStylesheet',
                    showExpandMinimizeBtns: true,
                    placeholder: 'This is where your CSS styles go.',
                    helpText: 'Create classes specific to this component'
                };
                if (hasSubPanels) ourConfig.commonStylesheetPanel.fields.push('componentCssStylesheet');
            }
            // Default Print Options
            if (typeof ourConfig.commonPrintSettingsPanel === 'undefined' && hasSubPanels) {
                ourConfig.commonPrintSettingsPanel = {
                    type: 'panel',
                    label: 'Common Print Settings',
                    id: 'commonPrintSettingsPanel',
                    fields: []
                };
            }
            if (typeof ourConfig.enablePrinting === 'undefined') {
                ourConfig.enablePrinting = {
                    label: 'Enable Printing',
                    type: 'boolean',
                    id: 'enableComponentPrinting',
                    message: 'Displays a button for this component that opens a printable view.',
                };
                if (hasSubPanels) ourConfig.commonPrintSettingsPanel.fields.push('enablePrinting');
            }
            if (typeof ourConfig.commonPrintStyles === 'undefined') {
                ourConfig.commonPrintStyles = {
                    label: 'Component Style Info (optional)',
                    type: 'text',
                    id: 'commonPrintStyles',
                    message: 'Apply custom styles for the printable view of this component.',
                    logic: [
                        {
                            record: 'self',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: 'this.valuesMap.enablePrinting'
                        }
                    ]
                };
                if (hasSubPanels) ourConfig.commonPrintSettingsPanel.fields.push('commonPrintStyles');
            }
            // Add default callbacks
            if (typeof ourConfig.templateFile !== 'undefined') {
                ourConfig.templateFile.onchange = 'changeTemplate';
            }
            // Set controls
            let setControls = () => {
                let actions = [];
                if (ourComponent.methods) {
                    Object.keys(ourComponent.methods).forEach((methodKey) => {
                        let functionParams = library.getFunctionParams(ourComponent.methods[methodKey]);
                        if (!functionParams.length) {
                            actions.push({
                                text: 'Component: ' + methodKey,
                                value: methodKey
                            });
                        }
                    });
                }
                for (let i in actionStore.actions) {
                    if (i === 'length') continue;
                    actions.push({
                        text: 'Action Store: ' + i,
                        value: 'actionStore.' + i
                    });
                }
                actions.push({
                    text: 'Set Layout Property',
                    value: 'layoutProperty.'
                });
                if (actions) {
                    ourConfig.controls.itemConfig.keys.onclick.items = actions;
                    ourConfig.controls.itemConfig.keys.onchange.items = actions;
                }
            };
            if (typeof ourConfig.controls !== 'undefined') {
                setControls();
            }
            this.editorConfig = ourConfig;
            this.positionJsonEditorMenu();
        },
        positionJsonEditorMenu() {
            const jsonEditor = this.$refs?.jsonEditor?.$el;
            if (jsonEditor) {
                // Watches for the json popup being added dynamically
                const observer = new MutationObserver((mutationsList) => {
                    for (const mutation of mutationsList) {
                        if (mutation.type === 'childList') {
                            const popup = jsonEditor.querySelector('.jse-absolute-popup-content');
                            if (popup) {
                                const left = +popup.style.left.split('px')[0];
                                popup.style.transform = left > 255 ? 'translate(-94%, 0%)' : 'translate(-0%, 0%)';
                            }
                        }
                    }
                });
                observer.observe(jsonEditor, { childList: true, subtree: true });
            }
        },
        setSettingsMap(thisSettings, editorConfig) {
            if (!thisSettings) return;
            let settings = library.cloneObj(thisSettings);
            let settingsMap = library.cloneObj(editorConfig);
            Object.keys(settingsMap).forEach((item) => {
                if (typeof settingsMap[item] === 'object') {
                    if (!settingsMap[item].column) settingsMap[item].column = 1;
                    if (settingsMap[item].type === 'ANY') settingsMap[item].type = settings[item];
                    if (typeof settings[item] !== 'undefined') {
                        settingsMap[item].value = this.convertDataType(settings[item], settingsMap[item].type);
                    } else if (settingsMap[item].default) {
                        settingsMap[item].value = settingsMap[item].default;
                    } else if (settingsMap[item].type.toLowerCase() === 'array') settings[item] = [];
                } else {
                    if (typeof settings[item] === 'undefined') {
                        settings[item] = settingsMap[item];
                    }
                }
                if (settingsMap[item]?.panel) { // inserts the setting into an existing panel (for global panels)
                    let panelName = settingsMap[item].panel;
                    settingsMap[panelName].fields.push(settingsMap[item].name || settingsMap[item].id);
                }
            });
            settingsMap = this.getEditorOptionsData(settings, settingsMap);
            if (settings.lockedConfig) this.isLocked = true;
            this.settingsMap = settingsMap;
            this.$forceUpdate();
        },
        // setDefaults(settings, settingsObj) {
        //     let configObj = library.cloneObj(settingsObj);
        //     let recurse = function (rObj, sObj) {
        //         let entries = Object.entries(sObj);
        //         // let s2 = Object.entries(sObj).filter((item) => typeof item[1] === 'object' && item[1] !== null);
        //         let e2 = entries.filter((item) => typeof item[1] === 'object' && item[1] !== null);
        //         // Recurse through sub objects
        //         e2.forEach((item) => {
        //             let type = item[1].type.toLowerCase();
        //             let keyConfig;
        //             if (type === 'object') keyConfig = item[1].properties;
        //             else keyConfig = item[1].itemConfig && item[1].itemConfig.keys;
        //             let prop = item[0];
        //             if (type === 'object') {
        //                 if (typeof rObj[prop] !== 'object') rObj[prop] = item[1].default || {};
        //                 if (keyConfig) recurse(rObj[prop], keyConfig);
        //             } else if (type === 'array') {
        //                 if (keyConfig && rObj) {
        //                     if (!Array.isArray(rObj[prop])) rObj[prop] = [];
        //                     let len = rObj[prop].length;
        //                     for (let index = 0; index < len; index++) {
        //                         recurse(rObj[prop][index], keyConfig);
        //                     }
        //                 }
        //             } else {
        //                 if (typeof rObj !== 'undefined' && (
        //                     typeof rObj[prop] === 'undefined' || rObj[prop] === null)) {
        //                     // Set Dropdowns to null if the default is false or "" or undefined
        //                     if (!item[1].default && item[1].default !== null && (type === 'dropdown' || type === 'number' || type === 'date')) {
        //                         rObj[prop] = null;
        //                         this.errorHandler.log('Default for ' + item[1].id + ': is invalid.');
        //                     }
        //                     else if (typeof item[1].default !== 'undefined') rObj[prop] = item[1].default;
        //                 }
        //             }
        //         });
        //     };
        //     recurse(settings, configObj);
        //     return settings;
        // },
        // setDefaultObjects(thisSettings) {
        //     let settings = library.cloneObj(thisSettings);
        //     let sMap = this.settingsMap;
        //     let keys = Object.keys(sMap);
        //     let setObjKeys = (key) => {
        //         if (!sMap[key].default) {
        //             this.errorHandler.log('no default for object ' + key);
        //             return;
        //         }
        //         if (!settings[key] || typeof settings[key] !== 'object') settings[key] = library.cloneObj(sMap[key].default);
        //         else if (settings[key] && typeof settings[key] === 'object') {
        //             let keys2 = Object.keys(sMap[key].default);
        //             keys2.forEach((sMapObjKey) => {
        //                 if (typeof settings[key][sMapObjKey] === 'undefined') {
        //                     settings[key][sMapObjKey] = sMap[key][sMapObjKey];
        //                 }
        //             });
        //         }
        //     };
        //     keys.forEach((key) => {
        //         if (sMap[key].type === 'OBJECT') setObjKeys(key);
        //     });
        //     return settings;
        // },
        getEditorOptionsData(settings, settingsMap) {
            let newSettingsMap = library.cloneObj(settingsMap);
            Object.keys(newSettingsMap).forEach((nsmKey) => {
                if (typeof newSettingsMap[nsmKey].type !== 'undefined' && newSettingsMap[nsmKey].type.toLowerCase() !== 'array' && typeof settings[nsmKey] !== 'undefined') {
                    newSettingsMap[nsmKey].value = settings[nsmKey];
                } else if (newSettingsMap[nsmKey].type.toLowerCase() === 'array') {
                    newSettingsMap[nsmKey].items = settings[nsmKey];
                    newSettingsMap[nsmKey].itemConfig.componentId = this.componentId;
                }
            });
            return newSettingsMap;
        },
        importSettings() {
            // TODO set this up
        },
        exportSettings() {
            let settings = library.cloneObj(this.valuesMap);
            delete settings.cTemplate;
            delete settings.showInCmpList;
            delete settings.configLoaded;
            delete settings.fromDb;
            delete settings.isGlobal;
            delete settings.loaded;
            settings = JSON.stringify(settings);
            document.execCommand("copy");
            // Perform the copy
            navigator.clipboard.writeText(settings).then(() => {
                /* clipboard successfully set */
                library.showDialog("<div><strong>Export settings copied to the clipboard!</strong></div>",
                    () => {
                        this.okClick();
                    },
                    false,
                    {
                        title: 'Export Settings',
                        showAsAlert: true
                    });
            }, () => {
                /* clipboard write failed */
                library.showDialog("<div><strong>Export settings could not be copied.</strong></div>",
                    () => {
                        this.okClick();
                    },
                    false,
                    {
                        title: 'Export Settings',
                        showAsAlert: true
                    });
            });
        },
        changeTemplate(e) {
            if (!e.itemData) return;
            let oldPositions = library.cloneObj(this.valuesMap.positions);
            let pCount = Number(e.itemData.positionCount);
            let tmpTemplateSettings = {
                newPositions: [],
                newTemplate: e.value,
            };
            e.itemData.positions.forEach((posOption, index) => {
                let iPosition = oldPositions.find((position) => position.posIndex === posOption.posIndex);
                if (iPosition) {
                    iPosition.label = posOption.label;
                    iPosition.posIndex = posOption.posIndex;
                } else {
                    iPosition = {
                        label: posOption.label || 'Position ' + index,
                        posIndex: posOption.posIndex,
                        components: []
                    };
                }
                tmpTemplateSettings.newPositions.push(iPosition);
            });

            // Move components from extra oldPositions to last newPosition item
            if ((oldPositions.length - pCount) > 0) {
                let newCompKey = tmpTemplateSettings.newPositions.length;
                let extraComponents = [];
                for (let posIdx = pCount; posIdx < oldPositions.length; posIdx++) {
                    let posComps = oldPositions[posIdx].components;
                    for (let compIdx = 0; compIdx < posComps.length; compIdx++) {
                        let movedComp = library.cloneObj(posComps[compIdx]);
                        ++newCompKey;
                        movedComp.key = newCompKey.toString();
                        movedComp.position = posIdx;
                        extraComponents.push(movedComp);
                    }
                }
                if (extraComponents.length) {
                    let mainIndex = 0;
                    tmpTemplateSettings.newPositions.forEach((position, index) => {
                        if (position.posIndex === 0) mainIndex = index;
                    });
                    tmpTemplateSettings.newPositions[mainIndex].components = tmpTemplateSettings.newPositions[mainIndex].components.concat(extraComponents);
                }
            }
            this.tmpTemplateSettings = tmpTemplateSettings;
        },
        setStaticPanelHeight() {
            // TODO move this to a custom service
            // Use an interval to get the watcher setup
            let panelSizeInterval = setInterval(() => {
                if (this.$refs && this.$refs.settingsContainer) {
                    let parentEl = this.$refs.settingsContainer.closest('#component-settings-sidebar-menu');
                    let sizeOffset = 110; // adjusts for tabs and bottom row of buttons
                    let maxDiff = 5;
                    let minDiff = 0;
                    const panelResizeObs = new ResizeObserver(() => {
                        let pElDimensions = parentEl.getBoundingClientRect();
                        let height = pElDimensions.bottom - pElDimensions.top;
                        let heightDiff = height - this.panelHeight + sizeOffset;
                        if (heightDiff < minDiff || heightDiff > maxDiff) {
                            this.panelHeight = height - sizeOffset;
                        }
                    });
                    clearInterval(panelSizeInterval);
                    if (parentEl) panelResizeObs.observe(parentEl);
                }
            }, 100, 100); // NOSONAR
        },
        onWindowResize() {
            this.windowHeight = window.innerHeight;
            this.windowWidth = window.innerWidth;
        }
    },
    updated() {
        // looking for settingsContainer
        this.updateLockedControls(false, true);
    },
    mounted() {
        // TODO move this to a custom service
        this.setStaticPanelHeight();
        this.$nextTick(() => {
            window.addEventListener('resize', this.onWindowResize);
        });
    },
    beforeUnmount() {
        this.checkForChanges();
        window.removeEventListener('resize', this.onWindowResize);
    },
    components: {
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
        "json-editor": JsonEditorVue
    }
};

componentStore.initComponent(settingsEditorComp, settingsEditorConfig);

export default settingsEditorComp;
</script>

<style lang="css">
.component-settings-container {
    background-color: #ddd;
    flex: 1 1 auto;
    display: flex;
    font-size: 16px;
}
.opened-editors {
    flex-grow: 1;
}
.component-settings-container.with-sub-panels {
    width: 100%;
    display: block;
    flex: none;
}
.component-settings {
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
    background-color: #fff;
    flex: 1 1 auto;
    height: 100%;
}
.component-settings .sub-container {
    overflow-y: auto;
    padding: 0;
}
.component-settings .sub-container.container-with-columns {
    overflow-y: auto;
    display: flex;
}
.settings-column {
    flex: 1;
    padding: 5px;
}

h1 {
    font-weight: normal;
}

.settings-header {
    position: relative;
    height: 60px;
    font-weight: 400 !important;
    display: flex;
    align-items: stretch;
    background-color: rgb(25, 118, 210);
    color: #ffffff;
}
.settings-header h1 {
    margin-top: 10px;
}
.right-action-area, .left-action-area {
    margin: 6px;
}
.right-action-area {
    text-align: right;
}
.right-action-area button {
    padding: 4px;
}
.settings-expander {
    margin-top: 8px;
    font-size: 13px;
}
.component-settings-editor {
    height: auto;
}
.component-settings-editor button {
    border: 0 none;
    background: transparent;
    cursor: pointer;
}
.component-settings-editor .e-icons.e-search:before {
    content: '\e993' !important;
}
.component-settings-editor .e-icons.e-cog:before {
    content: '\e679' !important;
}
.settings-header .e-icons {
    color: #fafafa;
    font-size: 14px;
}
.settings-header .e-icons.e-close {
    color: #fafafa;
}
.settings-header .e-icons {
    opacity: 0.6;
}
.settings-header .e-icons:hover {
    opacity: 1;
}
.json-editor {
    max-height: 100%;
    overflow-y: scroll;
}
.e-icons.e-close:before {
    content: '\ec0f' !important;
}
.e-icons.e-double-left-arrow:before {
    content: '\e656' !important;
}
.fa-solid.fa-brackets-curly {
    color: white;
    opacity: 0.6;
    font-size: 15px;
    position: relative;
    top: -1px;
}
.e-icons.e-double-right-arrow:before {
    content: '\e655' !important;
}
.e-icons.e-back-arrow:before {
    content: '\e977' !important;
}
.e-icons.e-left-arrow:before {
    content: '\e85b' !important;
}
.e-icons.e-right-arrow:before {
    content: '\e85f' !important;
}
.e-icons.e-left-chevron:before {
    content: '\e937' !important;
}
.e-icons.e-right-chevron:before {
    content: '\e956' !important;
}
.e-icons.e-menu-1:before {
    content: '\e99a' !important;
}
.e-icons.e-menu-2:before {
    content: '\e984' !important;
}
.e-icons.e-search:before {
    content: '\e275' !important;
}
button .dismiss-message_panel {
    font-size: 1.5rem !important;
    font-weight: 500;
    color: #1c1c1c;
}

.settings-title {
    font-size: 18px;
    flex-grow: 2;
    text-align: center;
}

/*noinspection CssUnusedSymbol*/
.e-tooltip-wrap {
    border-radius: 4px;
    opacity: 1;
}

/*noinspection CssUnusedSymbol*/
.e-tooltip-wrap .e-tip-content {
    font-size: 16px;
    line-height: 20px;
}

/*noinspection CssUnusedSymbol*/
.e-tooltip-wrap.e-popup {
    padding: 5px;
}

.final-action-area {
    background: var(--surface-default);
    border-top: 2px solid var(--border-color-default);
    display: flex;
    flex-direction: row;
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 5px 10px;
}

.final-action-area.with-sub-panels {
    height: 50px;
}

.final-action-area .button-area {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
}

.final-action-area .button-area div.e-control {
    margin-left: 10px;
    width: auto !important;
}

.final-action-area > div {
    flex-grow: 1;
}

.final-action-area .action-area-message {
    padding: 5px;
    font-size: 13px;
}

.lock-unlock {
    font-size: 2em;
    cursor: pointer;
    display: block;
    margin-top: 3px;
}

.lock-unlock svg {
    width: 1em;
    height: 1em;
}
button {
    cursor: pointer;
}
</style>
